import React, { useState, useMemo, useEffect } from "react";
import {
  GridRowsProp,
  GridRowSelectionModel,
  useGridApiRef,
  GridColDef,
} from "@mui/x-data-grid";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SxProps,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import StyledDataGrid from "./StyledDataGrid";
import { DataGridCustomToolbar } from "./UIParts/DataGridCustomToolbar";

export type AppGridColDef<T = any> = GridColDef<T> & {
  required?: boolean;
};

interface EditableDataGridStyleProps<T> {
  initialRows: T[];
  columns: AppGridColDef<T>[];
  onPartialUpdate: (id: number | string, fields: Partial<T>) => Promise<void>;
  onDeleteRows?: (ids: (number | string)[]) => Promise<void>;
  onCreateNew?: (fields: Partial<T>) => Promise<void>;
  onRowSelectionModelChange?: (ids: (string | number)[]) => void;
  onRowClick?: (params: any) => void; // ✅ חדש
  emptyRow?: Partial<T>;
  loading?: boolean;
  maxHeight?: string;
  startInEditMode?: boolean;
  getRowClassName?: (params: any) => string;
  sx?: SxProps;
  hideDeleteButton?: boolean; // ✅ חדש - להסתיר את כפתור המחיקה
}

export function EditableDataGridStyle<T extends { id?: number | string }>(props: EditableDataGridStyleProps<T>) {
  const {
    initialRows,
    columns,
    onPartialUpdate,
    onDeleteRows,
    onCreateNew,
    onRowSelectionModelChange,
    emptyRow,
    loading = false,
    maxHeight= "600px",
    startInEditMode = false,
    hideDeleteButton = false,
  } = props;

  const [rows, setRows] = useState<GridRowsProp<T>>(initialRows);
  const [editMode, setEditMode] = useState<boolean>(props.startInEditMode ?? false);
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [newRow, setNewRow] = useState<Partial<T> | null>(null);
  const [newRowValid, setNewRowValid] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const apiRef = useGridApiRef();

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  useEffect(() => {
    if (editMode && !newRow && emptyRow) {
      setNewRow({ ...emptyRow });
    }
    if (!editMode) {
      setNewRow(null);
    }
  }, [editMode, emptyRow]);

  useEffect(() => {
    if (!newRow) return;
    const missingRequired = columns.some((col) => {
      const value = (newRow as any)[col.field];
      return col.required && (!value || value === "");
    });
    setNewRowValid(!missingRequired);
  }, [newRow, columns]);

  const extendedRows = useMemo(() => {
    const filtered = rows.filter((r) => r.id !== -1);
    return newRow ? [...filtered, { ...newRow, id: -1 }] as any : filtered as any;
  }, [rows, newRow]);


const saveNewRow = async (row: T) => {
  if (!onCreateNew) return;
  try {
    await onCreateNew(row);
    setRows((prev) => [...prev, { ...row, id: Date.now() }]);
    setNewRow(emptyRow ? { ...emptyRow } : null);
  } catch (e: any) {
    console.error("❌ שגיאה ביצירת שורה חדשה:", e);
    alert("שמירת הרשומה נכשלה. נסה שוב.");
  }
};
  const saveButtonColumn: AppGridColDef = {
    field: "__actions__",
    headerName: "",
    renderCell: (params) => {
      if (params.id !== -1) return null;
      const row = params.row;
      return (
        <Button
          disabled={!newRowValid}
           variant={newRowValid ? "contained" : "outlined"}
           color={"secondary"}
          onClick={async () => {
           await saveNewRow(row as T);
          }}
        >
          שמור
        </Button>
      );
    },
    sortable: false,
    filterable: false,
    width: 100,
  };

  const enhancedColumns = useMemo(() => {
    const base = columns.map((col) => ({
      ...col,
      editable: editMode || newRow?.id === -1,
      cellClassName: (params: any) => {
        if (
          params.id === -1 &&
          col.required &&
          (!params.value || params.value === "")
        ) {
          return "cell-required";
        }
        return "";
      },
    }));
    return newRow ? [...base, saveButtonColumn] : base;
  }, [columns, editMode, newRow, newRowValid]);

  const handleSaveAndExit = async () => {
    
  if (newRow) {
    await saveNewRow(newRow as T);
    setEditMode(false);
  }
};
  const handleProcessRowUpdate = async (newRowData: T, oldRowData: T) => {
    const id = newRowData.id;
    if (id === -1) {
      setNewRow(newRowData);
      return newRowData;
    }

    const changedFields: Partial<T> = {};
    for (const key in newRowData) {
      if ((newRowData as any)[key] !== (oldRowData as any)[key]) {
        (changedFields as any)[key] = (newRowData as any)[key];
      }
    }
    setRows((prev) =>
      prev.map((row) => ((row as any).id === id ? { ...row, ...changedFields } : row))
    );
    if (Object.keys(changedFields).length > 0) {
      try {
        await onPartialUpdate(id!, changedFields);
        // הצלחה - הצג הודעה
        setSnackbar({
          open: true,
          message: "השמירה הצליחה",
          severity: "success",
        });
      } catch (err) {
        console.error("❌ שגיאה בעדכון לשרת:", err);
        // כשלון - הצג הודעת שגיאה
        setSnackbar({
          open: true,
          message: "השמירה נכשלה",
          severity: "error",
        });
        // החזר את הערך הישן
        setRows((prev) =>
          prev.map((row) => ((row as any).id === id ? oldRowData : row))
        );
        throw err; // זרוק את השגיאה כדי שה-DataGrid יטפל בה
      }
    }
    return { ...oldRowData, ...newRowData };
  };

  const handleEditToggle = () => setEditMode((prev) => !prev);

  const handleDelete = async () => {
    if (onDeleteRows) {
      await onDeleteRows(selectedIds);
      setRows((prev) => prev.filter((r) => !selectedIds.includes((r as any).id)));
      setSelectedIds([]);
    }
    setConfirmOpen(false);
  };

  return (
    <Box>
      <StyledDataGrid
        apiRef={apiRef}
        rows={extendedRows}
        columns={enhancedColumns}
        checkboxSelection={editMode}
        disableRowSelectionOnClick
        editMode={editMode ? "cell" : undefined}
        isCellEditable={(params) => editMode || params.id === -1}
        onRowClick={props.onRowClick}
        onCellClick={(params, event) => {
          // אם התא הוא editable ובמצב עריכה והתא במצב view, נכנס למצב עריכה
          if ((editMode || params.id === -1) && params.isEditable && params.cellMode === "view") {
            apiRef.current?.startCellEditMode({ id: params.id, field: params.field });
            // עצירת ה-propagation כדי שלא יקרא onRowClick
            event.stopPropagation();
          }
        }}
        getRowId={(row) => row.id!}
        processRowUpdate={handleProcessRowUpdate}
        onProcessRowUpdateError={(error) => console.error(error)}
        getRowClassName={props.getRowClassName}
        onRowSelectionModelChange={(ids: GridRowSelectionModel) => {
          setSelectedIds(Array.from(ids));
          onRowSelectionModelChange?.(Array.from(ids));
        }}
        slots={{
          toolbar: () => (
            <DataGridCustomToolbar
              editMode={editMode}
              selectedCount={editMode ? selectedIds.length : 0}
              onToggleEdit={handleEditToggle}
              onOpenConfirm={!hideDeleteButton && onDeleteRows ? () => setConfirmOpen(true) : undefined}
              newRowValid={newRowValid}
              newRowExists={!!newRow}
              onSaveAndExit={handleSaveAndExit}
            />
          ),
        }}
        maxHeight={maxHeight}
        loading={loading}
     
       sx={{
          ".MuiDataGrid-cell--editing": { backgroundColor: "#fff7e6" },
          "& .cell-required": { backgroundColor: "#ffe5e5" },
          ...(props.sx || {}), // ✅ הוספת sx חיצוני
        }}
      />

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>אישור מחיקה</DialogTitle>
        <DialogContent>
          <Typography>האם למחוק {selectedIds.length} רשומות?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>ביטול</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            מחק
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar להודעות הצלחה/כשלון */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          icon={snackbar.severity === "success" ? <CheckCircleIcon /> : <ErrorIcon />}
          sx={{
            width: "100%",
            fontSize: "1rem",
            fontWeight: "bold",
            "& .MuiAlert-icon": {
              fontSize: "2rem",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
